import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSplash } from '../../context/SplashContext';
import { MdBalance, MdGraphicEq } from 'react-icons/md';
import ResearchCrumb from './ResearchCrumb';
import CompanySearch from './CompanySearch';

// The hub — SEARCH-FIRST. ResearchOS is the umbrella, and the master way in is a company search:
// find a name and see everything held for it at once (scores, primer, chatter, and the feeds to
// come), assembled on one profile. The two mode cards below remain the way to BROWSE each source
// on its own terms.
//
// The search is the master surface, the modes are the components. They are NOT two views of one
// dataset — Quorum is a human-in-the-loop scoring pipeline whose output is a decision, Chatter an
// agentic read of public commentary that decides nothing. They arrive in the same daily push,
// which is a delivery detail, not a reason to blur them; the company profile places them side by
// side without merging them, and these cards keep each reachable on its own.
//
// BOTH MODES USE THE SIGNAL FIELD, NOT THE RADAR. The radar plays on the way INTO ResearchOS
// from the portal; using it again one screen later showed the same animation twice for two
// different moves, and the second stops reading as an arrival. The field is the other mark in
// the set, so the two steps stay distinguishable.

const MODES = [
  {
    to: '/research/quorum',
    Icon: MdBalance,
    label: 'Quorum',
    desc: 'Agents propose, the analyst decides',
    // Quorum's own line, describing what Quorum does. The combined "Agentic AI and in-house
    // intelligence" line belongs to ResearchOS as a whole (HomePage.js `enter()`) — the
    // umbrella and one mode within it are not the same claim, so they do not share a
    // sentence. Kept short: the splash types it a letter at a time and derives the pace from
    // the line's length, so a long line types too fast to read rather than being cut off.
    tagline: 'Agents propose. The analyst decides.',
  },
  {
    to: '/research/chatter',
    Icon: MdGraphicEq,
    label: 'Chatter',
    desc: 'Investor sentiment, aggregated',
    // The signal field, because that is what this mode is: many sources converging.
    tagline: 'Themes and trending names, from public conversation.',
  },
];

export default function ResearchOSHome() {
  const nav = useNavigate();
  const { playSplash } = useSplash();

  // Entering a mode gets the same transition as entering an area from the portal — the
  // splash carries the navigation, so the destination mounts under it and is revealed
  // rather than cut to.
  const enter = (m) => playSplash(() => nav(m.to), 5200,
    { spinner: !!m.spinner, tagline: m.tagline });

  return (
    <div>
      <ResearchCrumb />

      <div className="mb-5">
        <h1 className="font-serif text-2xl text-gray-900 dark:text-gray-100"
            style={{ letterSpacing: '0.02em' }}>
          Research<span className="text-brand-gold">OS</span>
        </h1>
      </div>

      {/* The master way in. A company profile combines every source; the browse cards below are
          the way to read each source on its own. */}
      <div className="mb-6">
        <CompanySearch />
        <p className="mt-2 text-[11px] text-gray-400">
          Search a company to see its scores, primer and chatter together.
        </p>
      </div>

      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
        Or browse a source
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {MODES.map((m) => (
          <button
            key={m.to} type="button" onClick={() => enter(m)}
            className="group flex flex-col gap-3 rounded-xl border border-gray-200 p-5 text-left transition-all hover:border-brand-gold hover:shadow-sm dark:border-gray-800"
          >
            <span className="grid h-11 w-11 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold transition-colors group-hover:bg-brand-gold group-hover:text-white">
              <m.Icon size={24} />
            </span>
            <div>
              <div className="font-serif text-lg text-gray-900 dark:text-gray-100">{m.label}</div>
              <div className="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                {m.desc}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
